// Lightweight fuzzy search for public menu.
// Zero dependencies. Handles typos, substring matches, category/description matches,
// and produces "did you mean" suggestions.

const normalize = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics (handles VN, FR, etc.)
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Damerau-Levenshtein (with transpositions). Short strings only; cheap.
const editDistance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const la = a.length;
  const lb = b.length;
  const d = Array.from({ length: la + 1 }, () => new Array(lb + 1).fill(0));
  for (let i = 0; i <= la; i++) d[i][0] = i;
  for (let j = 0; j <= lb; j++) d[0][j] = j;
  for (let i = 1; i <= la; i++) {
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[la][lb];
};

// Score a single item against a single query token. Higher = better match.
const scoreToken = (tok, hay) => {
  if (!tok || !hay) return 0;
  if (hay === tok) return 100;
  if (hay.startsWith(tok)) return 85;
  const idx = hay.indexOf(tok);
  if (idx >= 0) return 70 - Math.min(idx, 20); // earlier = better

  // Per-word prefix match (e.g. query "chi" matches "chicken soup")
  const words = hay.split(' ');
  for (const w of words) {
    if (w.startsWith(tok)) return 60;
    if (w.includes(tok) && tok.length >= 3) return 45;
  }

  // Fuzzy/typo tolerance against closest word
  if (tok.length >= 3) {
    let best = Infinity;
    for (const w of words) {
      if (Math.abs(w.length - tok.length) > 2) continue;
      const dist = editDistance(tok, w);
      if (dist < best) best = dist;
    }
    const maxDist = tok.length <= 4 ? 1 : tok.length <= 7 ? 2 : 3;
    if (best <= maxDist) return Math.max(10, 40 - best * 10);
  }
  return 0;
};

/**
 * Search items with fuzzy scoring.
 * @param {Array} items - list of menu items
 * @param {string} query - user query
 * @param {Function} getName - fn(item) -> current-language name
 * @returns {Array} items sorted by score, each with { item, score, matchedField }
 */
export const fuzzySearchItems = (items, query, getName) => {
  const q = normalize(query);
  if (!q) return items.map((item) => ({ item, score: 0, matchedField: null }));

  const tokens = q.split(' ').filter(Boolean);
  const scored = [];

  for (const item of items) {
    const nameAll = normalize(getName(item));
    const cat = normalize(item.category);
    const desc = normalize(item.description);
    const ingr = normalize(item.ingredients);

    let total = 0;
    let matchedField = null;

    for (const tok of tokens) {
      const sName = scoreToken(tok, nameAll);
      const sCat = scoreToken(tok, cat) * 0.6;
      const sDesc = scoreToken(tok, desc) * 0.45;
      const sIngr = scoreToken(tok, ingr) * 0.5;
      const best = Math.max(sName, sCat, sDesc, sIngr);
      if (best <= 0) {
        total = 0;
        break; // all tokens must contribute
      }
      total += best;
      if (!matchedField || best === sName) matchedField = 'name';
      else if (best === sCat * 0.6 / 0.6) matchedField = matchedField || 'category';
    }

    if (total > 0) scored.push({ item, score: total, matchedField });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored;
};

/**
 * Build "did you mean" suggestions when query yields few/no exact hits.
 * Returns up to `limit` suggested full names ordered by closeness.
 */
export const buildSuggestions = (items, query, getName, limit = 5) => {
  const q = normalize(query);
  if (!q || q.length < 2) return [];

  const allNames = new Set();
  items.forEach((it) => {
    const n = normalize(getName(it));
    if (n) allNames.add(n);
  });

  const ranked = [];
  for (const name of allNames) {
    // prefer names that contain the query
    if (name.includes(q)) {
      ranked.push({ name, score: 1000 - name.indexOf(q) });
      continue;
    }
    // otherwise score on word-level closeness
    const words = name.split(' ');
    let best = Infinity;
    for (const w of words) {
      if (Math.abs(w.length - q.length) > 3) continue;
      const d = editDistance(q, w);
      if (d < best) best = d;
    }
    if (best <= 3) ranked.push({ name, score: 500 - best * 50 });
  }

  ranked.sort((a, b) => b.score - a.score);
  // Map back to original (non-normalized) names
  const seen = new Set();
  const out = [];
  for (const r of ranked) {
    const orig = items.find((it) => normalize(getName(it)) === r.name);
    if (orig) {
      const display = getName(orig);
      if (!seen.has(display)) {
        seen.add(display);
        out.push(display);
        if (out.length >= limit) break;
      }
    }
  }
  return out;
};

/**
 * Split a display string into parts highlighting substrings matching query.
 * Returns array of { text, match: boolean } for easy rendering.
 */
export const highlightMatch = (display, query) => {
  if (!query) return [{ text: display, match: false }];
  const q = normalize(query);
  const normDisplay = normalize(display);
  const idx = normDisplay.indexOf(q);
  if (idx < 0) return [{ text: display, match: false }];

  // Map normalized index back to original display index (approximate: 1:1 for ASCII).
  // Since normalize strips diacritics/punctuation, we do a char walk.
  let origStart = -1;
  let origEnd = -1;
  let normCursor = 0;
  for (let i = 0; i < display.length; i++) {
    const normChar = normalize(display[i]);
    if (!normChar) continue;
    if (normCursor === idx && origStart === -1) origStart = i;
    normCursor += normChar.length;
    if (normCursor >= idx + q.length && origEnd === -1) {
      origEnd = i + 1;
      break;
    }
  }
  if (origStart < 0) return [{ text: display, match: false }];
  if (origEnd < 0) origEnd = display.length;

  return [
    { text: display.slice(0, origStart), match: false },
    { text: display.slice(origStart, origEnd), match: true },
    { text: display.slice(origEnd), match: false },
  ].filter((p) => p.text);
};

export { normalize };
